import { FC, useState } from 'react';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy as ContentCopyIcon, Sync as SyncIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { useInfoMessage } from '../../../../../hooks/useInfoMessage';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import ApiService from '../../../../../ApiService';
import Row from '../../../../../components/Row';
import { Platform } from '../../../../../types/Campaign';

interface Props {
    value: string;
    /** When given (with an Amazon DSP campaign), super admins get a "sync flights from meta" button. */
    campaignId?: number;
    platform?: string;
}

/** Mirrors CampaignMetaSyncService.COOLDOWN on the API. */
const META_SYNC_COOLDOWN_MINUTES = 10;

const DspCampaignIdValue: FC<Props> = ({ value, campaignId, platform }) => {
    const { showInfoMessage } = useInfoMessage();
    const { userContext } = useUserContext();
    const queryClient = useQueryClient();
    const [pending, setPending] = useState(false);
    const [nextAllowedAt, setNextAllowedAt] = useState<string | null>(null);

    const canSync = campaignId != null && platform === Platform.AMAZON && userContext.isSuperAdmin();
    const coolingDown = nextAllowedAt != null && moment().isBefore(moment(nextAllowedAt));
    const syncTitle = pending
        ? 'Syncing flights meta…'
        : coolingDown
        ? `Flights meta synced · next sync available ${moment(nextAllowedAt).fromNow()}`
        : 'Sync flights meta (up to 15 min behind the DSP)';

    const syncFromMeta = async (): Promise<void> => {
        if (campaignId == null) {
            return;
        }
        setPending(true);
        try {
            const { data } = await ApiService.syncCampaignFlightsFromMeta(campaignId);
            setNextAllowedAt(data.nextAllowedAt);
            const asOf = data.metaUpdatedAt ? ` Meta as of ${moment(data.metaUpdatedAt).fromNow()}.` : '';
            const nothingChanged = data.created + data.updated + data.deleted === 0;
            showInfoMessage(
                nothingChanged ? 'info' : 'success',
                nothingChanged
                    ? `Flights already match dsp_meta (${data.flightsInMeta} in DSP, nothing to change).${asOf}`
                    : `Flights synced from meta: ${data.flightsInMeta} in DSP, ${data.created} created, ` +
                          `${data.updated} updated, ${data.unchanged} unchanged, ${data.deleted} removed.${asOf}`
            );
            // Reload the campaign so the flights table shows what was just written; fall
            // back to a page reload where this id is not held by the campaign query.
            const campaignKey = ['campaign', campaignId];
            if (queryClient.getQueryState(campaignKey)) {
                await queryClient.refetchQueries({ queryKey: campaignKey, exact: true });
            } else {
                window.location.reload();
            }
        } catch (e: unknown) {
            const response = (e as { response?: { status?: number; data?: { message?: string; error?: string } } })
                .response;
            if (response?.status === 429) {
                setNextAllowedAt(moment().add(META_SYNC_COOLDOWN_MINUTES, 'minutes').toISOString());
            }
            showInfoMessage(
                'error',
                response?.data?.message || response?.data?.error || 'Flight sync from meta failed.'
            );
        } finally {
            setPending(false);
        }
    };

    return (
        <Row gap={0} alignItems="center">
            <Typography variant="caption" color="text.secondary">
                {value}
            </Typography>
            <IconButton
                aria-label="Copy Amazon DSP Campaign ID"
                edge="end"
                disableRipple
                title="Copy Amazon DSP Campaign ID"
                sx={{ fontSize: 16, py: 0 }}
                onClick={(): void => {
                    navigator.clipboard.writeText(value);

                    showInfoMessage('success', 'Copied Amazon DSP Campaign ID.');
                }}
            >
                <ContentCopyIcon fontSize="inherit" />
            </IconButton>
            {canSync && (
                <Tooltip title={syncTitle}>
                    <span>
                        <IconButton
                            aria-label="Sync flights meta"
                            edge="end"
                            disableRipple
                            disabled={pending || coolingDown}
                            sx={{ fontSize: 16, py: 0 }}
                            onClick={(): void => {
                                syncFromMeta();
                            }}
                        >
                            {pending ? <CircularProgress size={12} /> : <SyncIcon fontSize="inherit" />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Row>
    );
};

export type { Props as DspCampaignIdValueProps };
export default DspCampaignIdValue;
